import { useNavigate, useSearch } from "@tanstack/react-router";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { Badge } from "@/components/ui/badge";

import { useContractsRecord } from "../useContractsRecord";
import { RecordLink } from "./RecordLink";
import {
  useNewBusinessDetail,
  type KpiMonthRange,
  type KpiNewBusinessDetail,
} from "../api";
import type { KpiSearch } from "..";
import type { KpiPeriod } from "./PeriodSelector";
import { fmtUsd0 } from "../format";

export function NewBusinessDrawer({
  period,
  range,
  periodLabel,
}: {
  period: KpiPeriod;
  range: KpiMonthRange;
  periodLabel: string;
}) {
  const navigate = useNavigate();
  const search = useSearch({ from: "/kpi" });
  const open = search.tile === "new_business";
  const expanded = !!search.expanded;

  const go = (next: Partial<KpiSearch>) =>
    navigate({ to: "/kpi", search: (prev: KpiSearch) => ({ ...prev, ...next }) });

  const detail = useNewBusinessDetail(period, range, open);

  return (
    <EntityDrawer
      open={open}
      onOpenChange={(v) => {
        if (!v && !expanded) go({ tile: undefined, expanded: undefined });
      }}
      expanded={expanded}
      onExpandedChange={(exp) => open && go({ expanded: exp ? true : undefined })}
      onClose={() => {
        if (expanded) go({ expanded: undefined });
        else go({ tile: undefined, expanded: undefined });
      }}
      title="New — new + PLG business"
      subtitle={periodLabel}
    >
      {detail.isLoading ? (
        <div className="p-6 text-sm text-muted-foreground">Loading…</div>
      ) : !detail.data?.available ? (
        <div className="p-6 text-sm text-muted-foreground">No data for this period.</div>
      ) : expanded ? (
        <ExpandedView data={detail.data} />
      ) : (
        <QuickView data={detail.data} />
      )}
    </EntityDrawer>
  );
}

function KindChip({ kind }: { kind: "new" | "plg" | "po" }) {
  const meta = {
    plg: { variant: "info" as const, label: "PLG" },
    po: { variant: "warning" as const, label: "PO" },
    new: { variant: "success" as const, label: "New" },
  }[kind];
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

function Totals({ data }: { data: KpiNewBusinessDetail }) {
  const t = data.totals;
  return (
    <div className="border-b border-border p-4">
      <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">
        New + PLG contract ARR
      </div>
      <div className="text-3xl font-extrabold tracking-tight tabular-nums text-success">
        {fmtUsd0(t?.total_arr ?? 0)}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
        <Mini label={`New (${t?.new_contracts ?? 0})`} value={fmtUsd0(t?.new_arr)} />
        <Mini label={`PLG (${t?.plg_contracts ?? 0})`} value={fmtUsd0(t?.plg_arr)} />
        <Mini label="Avg deal" value={fmtUsd0(t?.avg_deal_arr ?? null)} />
        <Mini
          label={`Billed 1st time (${t?.first_billed_customers ?? 0})`}
          value={fmtUsd0(t?.first_billed_arr)}
        />
      </div>
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60">
        {label}
      </div>
      <div className="mt-0.5 font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function QuickView({ data }: { data: KpiNewBusinessDetail }) {
  const contracts = (data.contracts ?? []).slice(0, 10);
  return (
    <div>
      <Totals data={data} />
      <div className="p-4">
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Top signed contracts
        </div>
        <div className="mt-2 flex flex-col divide-y divide-border">
          {contracts.map((c) => (
            <div key={String(c.contract_id)} className="flex items-center justify-between gap-3 py-2">
              <div className="flex min-w-0 items-center gap-2">
                <KindChip kind={c.kind} />
                <span className="truncate text-sm">{c.account_name}</span>
              </div>
              <span className="flex-shrink-0 text-sm font-semibold tabular-nums">
                {fmtUsd0(c.arr)}
              </span>
            </div>
          ))}
          {contracts.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No contracts signed in this period.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ExpandedView({ data }: { data: KpiNewBusinessDetail }) {
  const { openContract, openAccount } = useContractsRecord();
  const contracts = data.contracts ?? [];
  const firstBilled = data.first_billed ?? [];
  return (
    <div>
      <Totals data={data} />
      <div className="grid grid-cols-1 gap-6 p-4 lg:grid-cols-2">
        <div>
          <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Signed contracts ({contracts.length})
          </div>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="px-2 py-1.5 text-left font-semibold">Account</th>
                <th className="px-2 py-1.5 text-left font-semibold">Type</th>
                <th className="px-2 py-1.5 text-left font-semibold">Signed</th>
                <th className="px-2 py-1.5 text-right font-semibold">ARR</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((c) => (
                <tr key={String(c.contract_id)} className="border-b border-border/60">
                  <td className="px-2 py-1.5">
                    <RecordLink
                      label={c.account_name}
                      onOpen={c.contract_id != null && (() => openContract(c.contract_id))}
                    />
                  </td>
                  <td className="px-2 py-1.5"><KindChip kind={c.kind} /></td>
                  <td className="px-2 py-1.5 tabular-nums text-muted-foreground">
                    {c.signed_date ?? "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{fmtUsd0(c.arr)}</td>
                </tr>
              ))}
              {contracts.length === 0 && (
                <EmptyRow cols={4} />
              )}
            </tbody>
          </table>
        </div>

        <div>
          <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Billed for the first time ({firstBilled.length})
          </div>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="px-2 py-1.5 text-left font-semibold">Account</th>
                <th className="px-2 py-1.5 text-left font-semibold">Month</th>
                <th className="px-2 py-1.5 text-right font-semibold">ARR</th>
              </tr>
            </thead>
            <tbody>
              {firstBilled.map((f) => (
                <tr key={String(f.qb_id)} className="border-b border-border/60">
                  <td className="px-2 py-1.5">
                    <RecordLink
                      label={f.account_name}
                      onOpen={f.account_id ? () => openAccount(f.account_id) : undefined}
                    />
                  </td>
                  <td className="px-2 py-1.5 tabular-nums text-muted-foreground">{f.month}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{fmtUsd0(f.arr)}</td>
                </tr>
              ))}
              {firstBilled.length === 0 && <EmptyRow cols={3} />}
            </tbody>
          </table>
          <div className="mt-2 text-[10px] text-muted-foreground">
            First-billed is revenue-waterfall <code>amount_new</code>; it can differ from signed
            contracts (billing starts after signing).
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyRow({ cols }: { cols: number }) {
  return (
    <tr>
      <td colSpan={cols} className="px-2 py-6 text-center text-sm text-muted-foreground">
        None in this period.
      </td>
    </tr>
  );
}
